import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Save, ClipboardList, User, Music, FileText,
  ShieldAlert, Palette, Sparkles, ListChecks, Users, Trash2, ExternalLink,
  Send, Undo2, Archive, Printer, Route as RouteIcon, CheckCircle2, Plus,
} from "lucide-react";
import { RightsPartySection } from "@/components/RightsParty";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { RouteToReviewDialog } from "@/components/RouteToReviewDialog";
import { PriorityBadge, PriorityTierControl } from "@/components/PriorityTierControl";

// ---------------------------------------------------------------------------
// MP12 Advanced Features — Module 2A: Internal Intake Workspace editor.
//
// One long-scroll editor with 8 sections A–H. Everything is provisional
// until the operator consciously routes it (Module 2B/3). Rights parties
// reuse the Phase 1 `RightsPartySection` component so there is exactly one
// canonical rights-party editor across the whole product.
//
// Manual "Save" is preserved (no autosave) so an operator on a live call
// controls exactly when partial data hits the DB. The editor also blocks
// the browser back button when there are unsaved changes so mid-call taps
// don't lose captured notes.
// ---------------------------------------------------------------------------

const SOURCE_OPTIONS = [
  { code: "phone",             label: "Phone" },
  { code: "video",             label: "Video call" },
  { code: "in_person",         label: "In person" },
  { code: "client_portal",     label: "Client portal" },
  { code: "uploaded_form",     label: "Uploaded form" },
  { code: "internal_referral", label: "Internal referral" },
  { code: "other",             label: "Other" },
];

const STATUS_OPTIONS = [
  { code: "draft",     label: "Draft" },
  { code: "in_review", label: "In Review" },
  { code: "routed",    label: "Routed (via review)" },
  { code: "archived",  label: "Archived" },
];

// Scope flags per user-provided intake scope taxonomy.
const SCOPE_FLAGS = [
  { code: "single_track",       label: "Single track" },
  { code: "ep",                 label: "EP" },
  { code: "album",              label: "Album" },
  { code: "catalogue",          label: "Catalogue" },
  { code: "master_only",        label: "Master-only issue" },
  { code: "composition_only",   label: "Composition-only issue" },
  { code: "publishing_admin",   label: "Publishing administration" },
  { code: "sync_readiness",     label: "Sync-readiness" },
  { code: "release_readiness",  label: "Release-readiness" },
  { code: "clearance",          label: "Clearance" },
  { code: "registration_filing","label": "Registration / filing preparation" },
  { code: "consultation_only",  label: "Consultation only" },
];

const SECTION_STATE_OPTIONS = [
  { code: "provisional",       label: "Provisional" },
  { code: "client_supplied",   label: "Client-supplied" },
  { code: "operator_observed", label: "Operator-observed" },
  { code: "document_supported","label": "Document-supported" },
  { code: "verified",          label: "Verified" },
  { code: "unresolved",        label: "Unresolved" },
];

const HAVE_STATUSES = ["HAVE", "NOT_PROVIDED", "NOT_APPLICABLE", "NEEDS_REVIEW", "DEFICIENT", "VERIFIED"];

const HAVE_STATUS_STYLES = {
  HAVE:           { bg: "hsl(150 40% 92%)", fg: "hsl(150 45% 25%)", border: "hsl(150 40% 70%)" },
  NOT_PROVIDED:   { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  NOT_APPLICABLE: { bg: "hsl(210 10% 94%)", fg: "hsl(210 8% 42%)",  border: "hsl(210 8% 78%)" },
  NEEDS_REVIEW:   { bg: "hsl(40 55% 92%)",  fg: "hsl(35 60% 30%)",  border: "hsl(40 55% 74%)" },
  DEFICIENT:      { bg: "hsl(0 60% 94%)",   fg: "hsl(0 65% 32%)",   border: "hsl(0 55% 74%)" },
  VERIFIED:       { bg: "hsl(190 60% 92%)", fg: "hsl(190 60% 28%)", border: "hsl(190 55% 72%)" },
};

function HaveStatusChip({ value }) {
  const st = HAVE_STATUS_STYLES[value] || HAVE_STATUS_STYLES.NOT_PROVIDED;
  return (
    <span
      className="text-[9.5px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
      style={{ background: st.bg, color: st.fg, border: `1px solid ${st.border}` }}
    >
      {value}
    </span>
  );
}

function SectionCard({ id, icon: Icon, title, hint, state, onStateChange, testid, children }) {
  return (
    <Card id={id} data-testid={testid} className="scroll-mt-24">
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
          <CardTitle className="text-[15px] flex-1 min-w-0">{title}</CardTitle>
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Section state</Label>
            <Select value={state || "provisional"} onValueChange={onStateChange}>
              <SelectTrigger className="h-7 w-[180px] text-[11.5px]" data-testid={`${testid}-state`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SECTION_STATE_OPTIONS.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {hint && <div className="text-[11.5px] text-muted-foreground mt-1">{hint}</div>}
      </CardHeader>
      <CardContent className="p-4 pt-2 space-y-3">{children}</CardContent>
    </Card>
  );
}

function TextField({ label, value, onChange, placeholder, multiline, rows, className, testid, mono }) {
  const cls = `mt-1 ${mono ? "font-mono" : ""} ${className || ""}`;
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wide">{label}</Label>
      {multiline ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows || 3} placeholder={placeholder} className={cls} data-testid={testid} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} data-testid={testid} />
      )}
    </div>
  );
}

// AltTitlesEditor — comma/enter-driven chip list without adding a new dependency
function AltTitlesEditor({ value, onChange, testid }) {
  const list = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  function add() {
    const v = draft.trim();
    if (!v) return;
    if (list.includes(v)) { setDraft(""); return; }
    onChange([...list, v]);
    setDraft("");
  }
  function rm(i) { onChange(list.filter((_, idx) => idx !== i)); }
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {list.map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-secondary/70 border border-white/10">
            {t}
            <button type="button" onClick={() => rm(i)} className="opacity-60 hover:opacity-100" aria-label={`Remove ${t}`} data-testid={`${testid}-rm-${i}`}>
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          placeholder="Add an alternate title, then Enter"
          className="h-8 text-[12.5px]"
          data-testid={`${testid}-input`}
        />
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`${testid}-add`}>Add</Button>
      </div>
    </div>
  );
}

export default function IntakeWorkspaceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writer = canWrite(user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [linkedCons, setLinkedCons] = useState(null);
  const [routeOpen, setRouteOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const isRouted   = data?.status === "routed";
  const isArchived = data?.status === "archived";
  const isInReview = data?.status === "in_review";
  const isDraft    = data?.status === "draft";
  const readOnly   = isRouted || isArchived;   // no field edits once routed/archived (super_admin can still delete)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/intake-workspaces/${id}`);
      setData(r.data);
      setDirty(false);
      if (r.data.linked_consultation_id) {
        try {
          const c = await api.get(`/kandake/consultations/${r.data.linked_consultation_id}`);
          setLinkedCons(c.data);
        } catch { setLinkedCons(null); }
      } else {
        setLinkedCons(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load intake");
      navigate("/consultation-requests?tab=intake");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Track unload attempts while dirty so the operator doesn't lose captured notes.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  function patch(fields) {
    setData((d) => ({ ...(d || {}), ...fields }));
    setDirty(true);
  }
  function patchSub(key, fields) {
    setData((d) => ({ ...(d || {}), [key]: { ...((d && d[key]) || {}), ...fields } }));
    setDirty(true);
  }
  function setSectionState(section, value) {
    setData((d) => ({
      ...(d || {}),
      section_states: { ...((d && d.section_states) || {}), [section]: value },
    }));
    setDirty(true);
  }
  function toggleScopeFlag(code) {
    setData((d) => {
      const flags = ((d && d.scope_flags) || []).slice();
      const i = flags.indexOf(code);
      if (i >= 0) flags.splice(i, 1);
      else flags.push(code);
      return { ...(d || {}), scope_flags: flags };
    });
    setDirty(true);
  }
  function patchChecklistItem(itemId, fields) {
    setData((d) => {
      if (!d) return d;
      const list = (d.checklist || []).map((it) =>
        it.id === itemId ? { ...it, ...fields, updated_at: new Date().toISOString() } : it
      );
      return { ...d, checklist: list };
    });
    setDirty(true);
  }
  function addOtherChecklist() {
    setData((d) => ({
      ...(d || {}),
      checklist: [
        ...((d && d.checklist) || []),
        {
          id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()),
          category: "other",
          label: "Other document",
          status: "NOT_PROVIDED",
          notes: "",
          updated_at: new Date().toISOString(),
          updated_by: user?.email || "",
        },
      ],
    }));
    setDirty(true);
  }
  function removeChecklistItem(itemId) {
    setData((d) => ({
      ...(d || {}),
      checklist: ((d && d.checklist) || []).filter((it) => it.id !== itemId),
    }));
    setDirty(true);
  }

  // --------- Module 2B: task list editors -----------------------------------
  function addTask() {
    setData((d) => ({
      ...(d || {}),
      tasks: [
        ...((d && d.tasks) || []),
        {
          id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()),
          title: "",
          detail: "",
          owner: "",
          due_hint: "",
          status: "open",
          priority: "normal",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: user?.email || "",
        },
      ],
    }));
    setDirty(true);
  }
  function patchTask(taskId, fields) {
    setData((d) => {
      if (!d) return d;
      const list = (d.tasks || []).map((t) => t.id === taskId ? { ...t, ...fields, updated_at: new Date().toISOString() } : t);
      return { ...d, tasks: list };
    });
    setDirty(true);
  }
  function removeTask(taskId) {
    setData((d) => ({
      ...(d || {}),
      tasks: ((d && d.tasks) || []).filter((t) => t.id !== taskId),
    }));
    setDirty(true);
  }

  // --------- Module 2B: lifecycle transitions -------------------------------
  async function submitForReview() {
    if (statusBusy) return;
    if (dirty) {
      toast.error("Save your changes before submitting for review.");
      return;
    }
    setStatusBusy(true);
    try {
      const r = await api.post(`/intake-workspaces/${data.id}/submit-for-review`, {
        review_notes: data.review_notes || "",
      });
      setData(r.data);
      toast.success("Submitted for review.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit for review.");
    } finally {
      setStatusBusy(false);
    }
  }
  async function returnToDraft() {
    if (statusBusy) return;
    const reason = window.prompt("Reason for returning to draft (optional):", "") || "";
    setStatusBusy(true);
    try {
      const r = await api.post(`/intake-workspaces/${data.id}/return-to-draft`, { reason });
      setData(r.data);
      toast.success("Returned to draft.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not return to draft.");
    } finally {
      setStatusBusy(false);
    }
  }
  async function archiveWorkspace() {
    if (statusBusy) return;
    if (!window.confirm("Archive this intake workspace? It will become read-only.")) return;
    const reason = window.prompt("Reason for archiving (optional):", "") || "";
    setStatusBusy(true);
    try {
      const r = await api.post(`/intake-workspaces/${data.id}/archive`, { reason });
      setData(r.data);
      toast.success("Intake archived.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive intake.");
    } finally {
      setStatusBusy(false);
    }
  }
  function handleRouted(fresh) {
    setData(fresh);
  }

  async function save() {
    if (!data || saving) return;
    setSaving(true);
    try {
      // Send only the fields the operator can edit here — server preserves the rest.
      const payload = {
        status: data.status,
        intake_source: data.intake_source,
        linked_consultation_id: data.linked_consultation_id || "",
        client_name: data.client_name || "",
        entity_name: data.entity_name || "",
        cisn_ref: data.cisn_ref || "",
        contact_method: data.contact_method || "",
        contact_preference: data.contact_preference || "",
        relationship_to_asset: data.relationship_to_asset || "",
        authority_claimed: data.authority_claimed || "",
        followup_time_pref: data.followup_time_pref || "",
        scope_flags: data.scope_flags || [],
        scope_other_note: data.scope_other_note || "",
        asset_title: data.asset_title || "",
        asset_alternate_titles: data.asset_alternate_titles || [],
        asset_version: data.asset_version || "",
        asset_artist: data.asset_artist || "",
        asset_featured_artist: data.asset_featured_artist || "",
        asset_isrc: data.asset_isrc || "",
        asset_iswc: data.asset_iswc || "",
        asset_upc_ean: data.asset_upc_ean || "",
        asset_release_state: data.asset_release_state || "",
        master_ownership_notes: data.master_ownership_notes || "",
        composition_ownership_notes: data.composition_ownership_notes || "",
        asset_pathway_status_note: data.asset_pathway_status_note || "",
        related_recordings: data.related_recordings || "",
        writer_parties: data.writer_parties || [],
        publisher_parties: data.publisher_parties || [],
        administrator_parties: data.administrator_parties || [],
        rightsholder_parties: data.rightsholder_parties || [],
        rights_notes: data.rights_notes || "",
        checklist: data.checklist || [],
        ai_provenance: data.ai_provenance || {},
        artwork: data.artwork || {},
        recommended: data.recommended || {},
        tasks: data.tasks || [],
        review_notes: data.review_notes || "",
        section_states: data.section_states || {},
      };
      const r = await api.patch(`/intake-workspaces/${id}`, payload);
      setData(r.data);
      setDirty(false);
      toast.success("Intake saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save intake");
    } finally {
      setSaving(false);
    }
  }

  const checklistProgress = useMemo(() => {
    const items = (data && data.checklist) || [];
    const total = items.length || 1;
    const known = items.filter((i) => ["HAVE", "VERIFIED", "NOT_APPLICABLE", "DEFICIENT"].includes(i.status)).length;
    return Math.round((known / total) * 100);
  }, [data]);

  if (!writer) {
    return (
      <div className="p-6 text-[13px] text-muted-foreground" data-testid="intake-editor-readonly">
        Internal Intake Workspaces are visible to Catalog Manager, GFMP Reviewer, and Super Admin only.
      </div>
    );
  }
  if (loading || !data) {
    return <div className="p-6 text-[13px] text-muted-foreground">Loading intake…</div>;
  }

  const secState = data.section_states || {};

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[1080px]" data-testid="intake-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/consultation-requests?tab=intake")} data-testid="intake-editor-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to intake list
        </Button>
        <ClipboardList className="w-5 h-5 ml-2" style={{ color: "hsl(347 60% 22%)" }} />
        <h1 className="font-display text-[22px]">Internal Intake Workspace</h1>
        <Badge
          variant="outline"
          className="text-[10px] uppercase tracking-[0.14em] font-semibold"
          style={{ background: "hsl(40 55% 94%)", color: "hsl(35 60% 30%)", borderColor: "hsl(40 55% 78%)" }}
          data-testid="intake-provisional-badge"
        >
          Provisional
        </Badge>
        <PriorityBadge tier={data?.priority_tier} testid="intake-priority-badge" />
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {dirty && <span className="text-[11px] text-amber-800/80">Unsaved changes</span>}
          {readOnly && (
            <span className="text-[11px] text-muted-foreground italic">Read-only ({data.status})</span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(`/intake-workspaces/${data.id}/checklist`, "_blank", "noopener")}
            data-testid="intake-editor-print-checklist"
            title="Open print-ready Have/Have Not checklist"
          >
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print checklist
          </Button>
          {isDraft && (
            <Button
              size="sm"
              variant="outline"
              onClick={submitForReview}
              disabled={statusBusy || dirty}
              data-testid="intake-editor-submit-for-review"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              Submit for review
            </Button>
          )}
          {isInReview && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={returnToDraft}
                disabled={statusBusy}
                data-testid="intake-editor-return-to-draft"
              >
                <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                Return to draft
              </Button>
              <Button
                size="sm"
                onClick={() => setRouteOpen(true)}
                disabled={statusBusy || dirty}
                data-testid="intake-editor-open-route"
                style={{ background: "hsl(347 72% 22%)" }}
              >
                <RouteIcon className="w-3.5 h-3.5 mr-1.5" />
                Route to review
              </Button>
            </>
          )}
          {(isDraft || isInReview || isRouted) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={archiveWorkspace}
              disabled={statusBusy}
              data-testid="intake-editor-archive"
              title="Archive this workspace"
            >
              <Archive className="w-3.5 h-3.5 mr-1.5" />
              Archive
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={saving || !dirty || readOnly} data-testid="intake-editor-save" style={{ background: "hsl(347 72% 22%)" }}>
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Saving..." : "Save draft"}
          </Button>
        </div>
      </div>

      {/* Header meta card */}
      <Card>
        <CardContent className="p-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wide">Intake source</Label>
              <Select value={data.intake_source} onValueChange={(v) => patch({ intake_source: v })}>
                <SelectTrigger className="mt-1" data-testid="intake-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide">Status</Label>
              <Select value={data.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger className="mt-1" data-testid="intake-status-picker"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10.5px] text-muted-foreground mt-1">
                Moving beyond <em>Draft</em> does <strong>not</strong> auto-create verified records.
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide">Operator</Label>
              <Input value={data.operator_email || ""} readOnly className="mt-1 bg-secondary/50" data-testid="intake-operator" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide">Linked Consultation Request</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  value={data.linked_consultation_id || ""}
                  onChange={(e) => patch({ linked_consultation_id: e.target.value })}
                  placeholder="(optional consultation request id)"
                  className="font-mono text-[11.5px]"
                  data-testid="intake-linked"
                />
                {linkedCons && (
                  <Button asChild variant="ghost" size="sm" data-testid="intake-linked-open">
                    <Link to={`/consultation-requests/${data.linked_consultation_id}`}>
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  </Button>
                )}
              </div>
              {linkedCons && (
                <div className="text-[10.5px] text-muted-foreground mt-1">
                  {linkedCons.full_name}{linkedCons.org_name ? ` · ${linkedCons.org_name}` : ""}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Review notes (Module 2B) - visible when in_review or already routed */}
      {(isInReview || isRouted) && (
        <Card data-testid="intake-review-card">
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
              <CardTitle className="text-[15px]">Review notes</CardTitle>
              {isInReview && data.submitted_for_review_at && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  Submitted {new Date(data.submitted_for_review_at).toLocaleString()}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            <Textarea
              value={data.review_notes || ""}
              onChange={(e) => patch({ review_notes: e.target.value })}
              rows={3}
              readOnly={readOnly}
              placeholder="Reviewer's own notes about what still needs to be verified before routing."
              data-testid="intake-review-notes"
            />
            {data.submitted_for_review_by && (
              <div className="text-[10.5px] text-muted-foreground mt-1.5">
                Submitted for review by {data.submitted_for_review_by}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Routed summary card (Module 2B) - visible once the intake has been routed */}
      {isRouted && Array.isArray(data.routed_targets) && data.routed_targets.length > 0 && (
        <Card data-testid="intake-routed-summary">
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" style={{ color: "hsl(150 55% 25%)" }} />
              <CardTitle className="text-[15px]">Routed to review</CardTitle>
              <Badge
                variant="outline"
                className="text-[10px] uppercase tracking-[0.14em] font-semibold"
                style={{ background: "hsl(150 40% 92%)", color: "hsl(150 45% 25%)", borderColor: "hsl(150 40% 70%)" }}
              >
                {data.routed_targets.length} target{data.routed_targets.length === 1 ? "" : "s"}
              </Badge>
            </div>
            {data.routed_at && (
              <div className="text-[10.5px] text-muted-foreground mt-1">
                Routed by {data.routed_by || "?"} at {new Date(data.routed_at).toLocaleString()}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-1.5">
            {data.routing_reason && (
              <div className="text-[11.5px] italic text-muted-foreground">
                Reason: {data.routing_reason}
              </div>
            )}
            <ul className="text-[12.5px] space-y-1">
              {data.routed_targets.map((t, i) => (
                <li key={`${t.record_id}-${i}`} className="flex items-center gap-2" data-testid={`intake-routed-target-${t.kind}`}>
                  <Badge
                    variant="outline"
                    className="text-[10px] font-mono uppercase"
                  >
                    {t.kind}
                  </Badge>
                  <span>{t.label || t.record_id}</span>
                  {t.kind === "track" && t.record_id && (
                    <Link to={`/tracks/${t.record_id}`} className="ml-auto text-[11px] underline text-muted-foreground hover:text-foreground" data-testid={`intake-routed-open-${t.kind}`}>
                      Open candidate track
                    </Link>
                  )}
                  {t.kind === "consultation_followup" && t.record_id && (
                    <Link to={`/consultation-requests/${t.record_id}`} className="ml-auto text-[11px] underline text-muted-foreground hover:text-foreground" data-testid={`intake-routed-open-${t.kind}`}>
                      Open consultation
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Material Processing Priority — human-approved workflow support only.
          Does NOT bypass Asset Pathway, DEMO_ONLY, clearance, or lifecycle
          gating. Every change is audited with reason + material=true. */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Material Processing Priority</div>
            <div className="text-[11.5px] text-muted-foreground/90 mt-0.5">
              Sort / filter signal only. Assigning URGENT requires a documented compliance / legal reason.
            </div>
          </div>
          <PriorityTierControl
            record={data}
            endpoint={`/intake-workspaces/${data.id}/priority`}
            onChanged={() => load().catch(() => {})}
            canEdit={writer && !readOnly}
            compact
            testidPrefix="intake-priority"
            isSuperAdmin={user?.role === "super_admin"}
          />
        </CardContent>
      </Card>

      {/* A. Client & authority */}
      <SectionCard id="section-client" icon={User} title="A. Client & authority" testid="intake-section-client"
        state={secState.client} onStateChange={(v) => setSectionState("client", v)}
        hint="Who is the caller / uploader and what authority do they claim to speak with?">
        <div className="grid md:grid-cols-2 gap-3">
          <TextField label="Client name" value={data.client_name} onChange={(v) => patch({ client_name: v })} testid="intake-client-name" />
          <TextField label="Entity name (if any)" value={data.entity_name} onChange={(v) => patch({ entity_name: v })} testid="intake-entity-name" />
          <TextField label="CISN / internal identity ref" value={data.cisn_ref} onChange={(v) => patch({ cisn_ref: v })} mono testid="intake-cisn-ref" />
          <TextField label="Contact method" value={data.contact_method} onChange={(v) => patch({ contact_method: v })} placeholder="Email, phone, portal…" testid="intake-contact-method" />
          <TextField label="Contact preference" value={data.contact_preference} onChange={(v) => patch({ contact_preference: v })} testid="intake-contact-pref" />
          <TextField label="Relationship to asset" value={data.relationship_to_asset} onChange={(v) => patch({ relationship_to_asset: v })} testid="intake-relationship" />
          <TextField label="Authority / capacity claimed" value={data.authority_claimed} onChange={(v) => patch({ authority_claimed: v })} testid="intake-authority" />
          <TextField label="Best time / method for follow-up" value={data.followup_time_pref} onChange={(v) => patch({ followup_time_pref: v })} testid="intake-followup-pref" />
        </div>
      </SectionCard>

      {/* B. Intake scope */}
      <SectionCard id="section-scope" icon={ListChecks} title="B. Intake scope" testid="intake-section-scope"
        state={secState.scope} onStateChange={(v) => setSectionState("scope", v)}
        hint="Tick everything that applies. Provisional — do not treat as an activation.">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {SCOPE_FLAGS.map((f) => {
            const on = (data.scope_flags || []).includes(f.code);
            return (
              <label key={f.code} className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                <Checkbox checked={on} onCheckedChange={() => toggleScopeFlag(f.code)} data-testid={`intake-scope-${f.code}`} />
                <span>{f.label}</span>
              </label>
            );
          })}
        </div>
        <TextField label="Other scope note" value={data.scope_other_note} onChange={(v) => patch({ scope_other_note: v })} multiline rows={2} testid="intake-scope-other" />
      </SectionCard>

      {/* C. Asset & work */}
      <SectionCard id="section-asset" icon={Music} title="C. Asset & work information" testid="intake-section-asset"
        state={secState.asset} onStateChange={(v) => setSectionState("asset", v)}
        hint="Everything captured here is intake-provisional. It does not overwrite verified track records.">
        <div className="grid md:grid-cols-2 gap-3">
          <TextField label="Title" value={data.asset_title} onChange={(v) => patch({ asset_title: v })} testid="intake-asset-title" />
          <TextField label="Version" value={data.asset_version} onChange={(v) => patch({ asset_version: v })} testid="intake-asset-version" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide">Alternate titles</Label>
          <div className="mt-1">
            <AltTitlesEditor value={data.asset_alternate_titles} onChange={(v) => patch({ asset_alternate_titles: v })} testid="intake-alt-titles" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <TextField label="Artist" value={data.asset_artist} onChange={(v) => patch({ asset_artist: v })} testid="intake-asset-artist" />
          <TextField label="Featured artist" value={data.asset_featured_artist} onChange={(v) => patch({ asset_featured_artist: v })} testid="intake-asset-featured" />
          <TextField label="ISRC" value={data.asset_isrc} onChange={(v) => patch({ asset_isrc: v })} mono testid="intake-isrc" />
          <TextField label="ISWC" value={data.asset_iswc} onChange={(v) => patch({ asset_iswc: v })} mono testid="intake-iswc" />
          <TextField label="UPC / EAN" value={data.asset_upc_ean} onChange={(v) => patch({ asset_upc_ean: v })} mono testid="intake-upc" />
          <TextField label="Release state" value={data.asset_release_state} onChange={(v) => patch({ asset_release_state: v })} testid="intake-release-state" />
        </div>
        <TextField label="Master ownership notes" value={data.master_ownership_notes} onChange={(v) => patch({ master_ownership_notes: v })} multiline rows={2} testid="intake-master-notes" />
        <TextField label="Composition ownership notes" value={data.composition_ownership_notes} onChange={(v) => patch({ composition_ownership_notes: v })} multiline rows={2} testid="intake-comp-notes" />
        <TextField label="Asset pathway status note" value={data.asset_pathway_status_note} onChange={(v) => patch({ asset_pathway_status_note: v })} multiline rows={2} testid="intake-pathway-note" placeholder="e.g. Demo only / Development candidate / Clearance review / Client-ready — provisional per intake." />
        <TextField label="Related recordings / versions" value={data.related_recordings} onChange={(v) => patch({ related_recordings: v })} multiline rows={2} testid="intake-related" />
      </SectionCard>

      {/* D. Rights parties — reuse Phase 1 */}
      <SectionCard id="section-rights" icon={Users} title="D. Rights parties" testid="intake-section-rights"
        state={secState.rights} onStateChange={(v) => setSectionState("rights", v)}
        hint="Structured writer / publisher / administrator / rightsholder capture. Each party may hold its own PROs and IPI/CAE identifiers. Provisional — will not overwrite verified track parties.">
        <RightsPartySection
          label="Writers"
          partyType="writer"
          parties={data.writer_parties}
          onChange={(next) => patch({ writer_parties: next })}
          helperText="Each writer/composer can have multiple PROs and IPI/CAE identifiers."
          testid="intake-writer-parties"
        />
        <RightsPartySection
          label="Publishers"
          partyType="publisher"
          parties={data.publisher_parties}
          onChange={(next) => patch({ publisher_parties: next })}
          testid="intake-publisher-parties"
        />
        <RightsPartySection
          label="Publisher Administrators"
          partyType="administrator"
          parties={data.administrator_parties}
          onChange={(next) => patch({ administrator_parties: next })}
          testid="intake-administrator-parties"
        />
        <RightsPartySection
          label="Other Rightsholders"
          partyType="rightsholder"
          parties={data.rightsholder_parties}
          onChange={(next) => patch({ rightsholder_parties: next })}
          testid="intake-rightsholder-parties"
        />
        <TextField label="Rights notes / unresolved questions" value={data.rights_notes} onChange={(v) => patch({ rights_notes: v })} multiline rows={2} testid="intake-rights-notes" />
      </SectionCard>

      {/* E. Documents Have/Have-Not */}
      <SectionCard id="section-docs" icon={FileText} title="E. Documents · Have / Have Not" testid="intake-section-docs"
        state={secState.checklist} onStateChange={(v) => setSectionState("checklist", v)}
        hint={`Practical operator checklist. Every category defaults to NOT_PROVIDED. Progress: ${checklistProgress}%`}>
        <div className="divide-y border rounded-md" data-testid="intake-checklist">
          {(data.checklist || []).map((it) => (
            <div key={it.id} className="grid md:grid-cols-[1fr_180px_1fr_36px] gap-2 items-center px-2.5 py-2" data-testid={`intake-checklist-${it.category}`}>
              <div className="text-[12.5px]">
                {it.category === "other" ? (
                  <Input
                    value={it.label || ""}
                    onChange={(e) => patchChecklistItem(it.id, { label: e.target.value })}
                    placeholder="Custom document label"
                    className="h-7"
                    data-testid={`intake-checklist-label-${it.id}`}
                  />
                ) : (
                  <div className="font-medium">{it.label || it.category}</div>
                )}
                <div className="text-[10px] text-muted-foreground font-mono">{it.category}</div>
              </div>
              <div>
                <Select value={it.status} onValueChange={(v) => patchChecklistItem(it.id, { status: v })}>
                  <SelectTrigger className="h-7 text-[11.5px]" data-testid={`intake-checklist-status-${it.category}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HAVE_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        <span className="inline-flex items-center gap-2">
                          <HaveStatusChip value={s} />
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                value={it.notes || ""}
                onChange={(e) => patchChecklistItem(it.id, { notes: e.target.value })}
                placeholder="Notes (optional)"
                className="h-7 text-[12px]"
                data-testid={`intake-checklist-notes-${it.category}`}
              />
              {it.category === "other" ? (
                <button
                  type="button"
                  onClick={() => removeChecklistItem(it.id)}
                  className="h-7 w-7 rounded border text-muted-foreground hover:text-[hsl(0_70%_38%)] hover:bg-[hsl(0_70%_95%)]"
                  aria-label="Remove custom document row"
                  data-testid={`intake-checklist-remove-${it.id}`}
                >
                  <Trash2 className="w-3 h-3 mx-auto" />
                </button>
              ) : <div />}
            </div>
          ))}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={addOtherChecklist} data-testid="intake-checklist-add-other">
          + Add "Other" document row
        </Button>
      </SectionCard>

      {/* F. AI / provenance */}
      <SectionCard id="section-ai" icon={Sparkles} title="F. AI / provenance" testid="intake-section-ai"
        state={secState.ai} onStateChange={(v) => setSectionState("ai", v)}
        hint="Provisional intake information only. No automated system determines authorship or copyrightability here.">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="flex items-center justify-between rounded border p-2.5 text-[12.5px]">
            <span>AI involvement declared or suspected</span>
            <Switch checked={!!data.ai_provenance?.ai_involvement_declared} onCheckedChange={(v) => patchSub("ai_provenance", { ai_involvement_declared: !!v })} data-testid="intake-ai-declared" />
          </label>
          <label className="flex items-center justify-between rounded border p-2.5 text-[12.5px]">
            <span>Demo / reference only</span>
            <Switch checked={!!data.ai_provenance?.demo_reference_only} onCheckedChange={(v) => patchSub("ai_provenance", { demo_reference_only: !!v })} data-testid="intake-ai-demo-ref" />
          </label>
          <label className="flex items-center justify-between rounded border p-2.5 text-[12.5px]">
            <span>Re-performance / re-recording needed</span>
            <Switch checked={!!data.ai_provenance?.reperformance_needed} onCheckedChange={(v) => patchSub("ai_provenance", { reperformance_needed: !!v })} data-testid="intake-ai-reperformance" />
          </label>
        </div>
        <TextField label="AI role notes" value={data.ai_provenance?.ai_role_notes} onChange={(v) => patchSub("ai_provenance", { ai_role_notes: v })} multiline rows={2} testid="intake-ai-role" />
        <TextField label="Human authorship / performance status" value={data.ai_provenance?.human_authorship_status} onChange={(v) => patchSub("ai_provenance", { human_authorship_status: v })} multiline rows={2} testid="intake-ai-authorship" />
        <TextField label="Development needs" value={data.ai_provenance?.development_needs} onChange={(v) => patchSub("ai_provenance", { development_needs: v })} multiline rows={2} testid="intake-ai-dev-needs" />
        <TextField label="Provenance documents / explanations" value={data.ai_provenance?.provenance_docs_notes} onChange={(v) => patchSub("ai_provenance", { provenance_docs_notes: v })} multiline rows={2} testid="intake-ai-provenance-docs" />
        <TextField label="Human review outcome" value={data.ai_provenance?.human_review_outcome} onChange={(v) => patchSub("ai_provenance", { human_review_outcome: v })} multiline rows={2} testid="intake-ai-review-outcome" />
      </SectionCard>

      {/* G. Artwork */}
      <SectionCard id="section-artwork" icon={Palette} title="G. Artwork & presentation" testid="intake-section-artwork"
        state={secState.artwork} onStateChange={(v) => setSectionState("artwork", v)}
        hint="Compliance-support checklist only. Not a legal conclusion.">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="flex items-center justify-between rounded border p-2.5 text-[12.5px]">
            <span>Artwork present</span>
            <Switch checked={!!data.artwork?.artwork_present} onCheckedChange={(v) => patchSub("artwork", { artwork_present: !!v })} data-testid="intake-art-present" />
          </label>
          <TextField label="Artwork owner / creator" value={data.artwork?.artwork_owner} onChange={(v) => patchSub("artwork", { artwork_owner: v })} testid="intake-art-owner" />
        </div>
        <TextField label="Title / artist-name consistency" value={data.artwork?.title_consistency_status} onChange={(v) => patchSub("artwork", { title_consistency_status: v })} testid="intake-art-title-consistency" />
        <TextField label="Image / visual permissions" value={data.artwork?.image_permissions_notes} onChange={(v) => patchSub("artwork", { image_permissions_notes: v })} multiline rows={2} testid="intake-art-permissions" />
        <TextField label="Known third-party material" value={data.artwork?.known_third_party_material_notes} onChange={(v) => patchSub("artwork", { known_third_party_material_notes: v })} multiline rows={2} testid="intake-art-third-party" />
        <TextField label="Missing artwork deliverables" value={data.artwork?.missing_deliverables} onChange={(v) => patchSub("artwork", { missing_deliverables: v })} multiline rows={2} testid="intake-art-missing" />
        <TextField label="Artwork compliance concerns" value={data.artwork?.artwork_compliance_concerns} onChange={(v) => patchSub("artwork", { artwork_compliance_concerns: v })} multiline rows={2} testid="intake-art-concerns" />
      </SectionCard>

      {/* H. Recommended next action */}
      <SectionCard id="section-recommended" icon={ShieldAlert} title="H. Recommended next action" testid="intake-section-recommended"
        state={secState.recommended} onStateChange={(v) => setSectionState("recommended", v)}
        hint="Operator's internal working notes. Not a client-facing proposal.">
        <TextField label="Compliance deficiencies" value={data.recommended?.compliance_deficiencies} onChange={(v) => patchSub("recommended", { compliance_deficiencies: v })} multiline rows={2} testid="intake-rec-deficiencies" />
        <TextField label="Suggested task sequence" value={data.recommended?.suggested_task_sequence} onChange={(v) => patchSub("recommended", { suggested_task_sequence: v })} multiline rows={2} testid="intake-rec-sequence" />
        <div className="grid md:grid-cols-2 gap-3">
          <TextField label="Likely service lane" value={data.recommended?.likely_service_lane} onChange={(v) => patchSub("recommended", { likely_service_lane: v })} testid="intake-rec-service-lane" />
          <TextField label="Responsible operator" value={data.recommended?.responsible_operator} onChange={(v) => patchSub("recommended", { responsible_operator: v })} testid="intake-rec-responsible" />
          <TextField label="Follow-up date" value={data.recommended?.followup_date} onChange={(v) => patchSub("recommended", { followup_date: v })} placeholder="YYYY-MM-DD or freeform" testid="intake-rec-followup-date" />
        </div>
        <TextField label="Needed documents" value={data.recommended?.needed_documents} onChange={(v) => patchSub("recommended", { needed_documents: v })} multiline rows={2} testid="intake-rec-needed-docs" />
        <TextField label="Internal notes" value={data.recommended?.internal_notes} onChange={(v) => patchSub("recommended", { internal_notes: v })} multiline rows={3} testid="intake-rec-internal-notes" />
        <TextField label="Draft scope / proposal recommendation" value={data.recommended?.draft_scope_recommendation} onChange={(v) => patchSub("recommended", { draft_scope_recommendation: v })} multiline rows={3} testid="intake-rec-draft-scope" />
      </SectionCard>

      {/* H'. Draft task list (Module 2B) */}
      <Card id="section-tasks" data-testid="intake-section-tasks" className="scroll-mt-24">
        <CardHeader className="p-4 pb-2">
          <div className="flex flex-wrap items-center gap-2">
            <ListChecks className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
            <CardTitle className="text-[15px] flex-1 min-w-0">Draft task list &amp; follow-up actions</CardTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addTask}
              disabled={readOnly}
              data-testid="intake-tasks-add"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add task
            </Button>
          </div>
          <div className="text-[11.5px] text-muted-foreground mt-1">
            Human-editable follow-up list. When you route this intake, you can choose to persist the open tasks as standalone follow-up records.
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-2 space-y-2">
          {(data.tasks || []).length === 0 && (
            <div className="text-[12px] text-muted-foreground italic" data-testid="intake-tasks-empty">
              No tasks yet. Add follow-up items the operator wants to track.
            </div>
          )}
          {(data.tasks || []).map((t) => (
            <div key={t.id} className="border rounded p-2.5 grid md:grid-cols-[1fr_140px_120px_36px] gap-2 items-start" data-testid={`intake-task-${t.id}`}>
              <div className="space-y-1.5 md:col-span-1">
                <Input
                  value={t.title || ""}
                  onChange={(e) => patchTask(t.id, { title: e.target.value })}
                  placeholder="Task title"
                  className="h-8 text-[12.5px] font-medium"
                  readOnly={readOnly}
                  data-testid={`intake-task-title-${t.id}`}
                />
                <Textarea
                  value={t.detail || ""}
                  onChange={(e) => patchTask(t.id, { detail: e.target.value })}
                  placeholder="Detail / context (optional)"
                  rows={2}
                  className="text-[12px]"
                  readOnly={readOnly}
                  data-testid={`intake-task-detail-${t.id}`}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={t.owner || ""}
                    onChange={(e) => patchTask(t.id, { owner: e.target.value })}
                    placeholder="Owner (email or name)"
                    className="h-7 text-[11.5px]"
                    readOnly={readOnly}
                    data-testid={`intake-task-owner-${t.id}`}
                  />
                  <Input
                    value={t.due_hint || ""}
                    onChange={(e) => patchTask(t.id, { due_hint: e.target.value })}
                    placeholder="Due hint (e.g. next call)"
                    className="h-7 text-[11.5px]"
                    readOnly={readOnly}
                    data-testid={`intake-task-due-${t.id}`}
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide">Status</Label>
                <Select value={t.status || "open"} onValueChange={(v) => patchTask(t.id, { status: v })} disabled={readOnly}>
                  <SelectTrigger className="h-8 text-[12px] mt-0.5" data-testid={`intake-task-status-${t.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide">Priority</Label>
                <Select value={t.priority || "normal"} onValueChange={(v) => patchTask(t.id, { priority: v })} disabled={readOnly}>
                  <SelectTrigger className="h-8 text-[12px] mt-0.5" data-testid={`intake-task-priority-${t.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={() => removeTask(t.id)}
                disabled={readOnly}
                className="mt-5 h-7 w-7 rounded border text-muted-foreground hover:text-[hsl(0_70%_38%)] hover:bg-[hsl(0_70%_95%)] disabled:opacity-40"
                aria-label="Remove task"
                data-testid={`intake-task-remove-${t.id}`}
              >
                <Trash2 className="w-3 h-3 mx-auto" />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Danger zone */}
      <DeleteRecordButton
        recordType="intake_workspace"
        recordLabel="Intake Workspace"
        endpoint={`/intake-workspaces/${data.id}`}
        confirmToken={(data.id || "").slice(0, 8).toUpperCase()}
        confirmTokenLabel="short id"
        disabled={data.status !== "draft" && user?.role !== "super_admin"}
        disabledReason={
          data.status !== "draft"
            ? "Only draft workspaces can be deleted through ordinary controls. Super Admin can override."
            : ""
        }
        onDeleted={() => navigate("/consultation-requests?tab=intake")}
        testidPrefix="intake-delete"
      />

      <RouteToReviewDialog
        open={routeOpen}
        onOpenChange={setRouteOpen}
        workspace={data}
        onRouted={handleRouted}
      />
    </div>
  );
}
